/**
 * Anchor Links Section
 * - Fixes the bar to the viewport top (top: 0) after scrolling past its original position
 * - Scroll-spy: highlights the link for the section nearest the top activation line
 * - Hides the bar once the user has scrolled past the last anchor target (e.g. FAQ)
 */
;(function () {
  var ACTIVE_CLASS = 'oddit-section-anchor-links__link--active'
  var IS_FIXED_CLASS = 'oddit-section-anchor-links--is-fixed'
  var IS_HIDDEN_CLASS = 'oddit-section-anchor-links--is-hidden'

  /** Extra space below stacked chrome when scrolling to in-page targets (not full section padding). */
  var ANCHOR_SCROLL_GAP_PX = 8

  /**
   * Theme only applies `position: sticky` when `oddit-header--sticky` is present (header section setting).
   * Using computed `position` alone can mis-detect; static headers must contribute 0 to scroll-margin.
   */
  function getStackedHeaderHeightPx() {
    var header = document.querySelector('.oddit-header')
    if (!header || !header.classList.contains('oddit-header--sticky')) return 0
    var headerGroup = document.querySelector('.shopify-section-group-header-group')
    if (headerGroup) return headerGroup.offsetHeight
    return header.offsetHeight
  }

  function setActiveLink(links, targetLink) {
    links.forEach(function (l) {
      l.classList.remove(ACTIVE_CLASS)
    })
    if (targetLink) targetLink.classList.add(ACTIVE_CLASS)
  }

  /**
   * Viewport inset for scroll-margin.
   * When the bar is fixed it shares y=0 with the sticky header but sits under it (lower z-index).
   * Height must be max(header, bar), not sum — summing double-counts overlapping chrome and creates a huge gap.
   */
  function anchorViewportInsetPx(section, isFixed) {
    var headerH = getStackedHeaderHeightPx()
    if (isFixed) return Math.max(headerH, section.offsetHeight)
    return headerH
  }

  function syncAnchorScrollMargin(section, isFixed) {
    var px = anchorViewportInsetPx(section, isFixed) + ANCHOR_SCROLL_GAP_PX
    document.documentElement.style.setProperty('--oddit-anchor-scroll-margin', px + 'px')
  }

  function sortTargetsInDocumentOrder(list) {
    return list.slice().sort(function (a, b) {
      if (a === b) return 0
      var pos = a.compareDocumentPosition(b)
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1
      if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1
      return 0
    })
  }

  function readScrollMarginTopPx(el) {
    if (!el) return NaN
    var raw = window.getComputedStyle(el).scrollMarginTop
    var px = parseFloat(raw)
    return isNaN(px) ? NaN : px
  }

  function init(container) {
    var section = container || document.querySelector('.oddit-section-anchor-links')
    if (!section) return

    var nav = section.querySelector('.oddit-section-anchor-links__nav')
    if (!nav) return

    var links = Array.from(nav.querySelectorAll('.oddit-section-anchor-links__link'))
    if (!links.length) return

    var isSticky = section.classList.contains('oddit-section-anchor-links--sticky')
    var isFixed = false

    var spacer = section.previousElementSibling
    if (!spacer || !spacer.classList.contains('oddit-section-anchor-links__spacer')) {
      spacer = null
    }

    var targets = []
    var linkMap = {}

    links.forEach(function (link) {
      var href = (link.getAttribute('href') || '').trim()
      if (href && href !== '#') {
        var el = document.querySelector(href)
        if (el) {
          targets.push(el)
          linkMap[href] = link
        }
      }
    })

    targets = sortTargetsInDocumentOrder(targets)
    var lastTarget = targets.length ? targets[targets.length - 1] : null

    /** While set, scroll-spy does not override the link clicked (smooth scroll fires many scroll events). */
    var navClickLockHref = null
    var navClickLockTimer = null
    var navClickScrollEndHandler = null

    function clearNavClickLock() {
      navClickLockHref = null
      if (navClickLockTimer) {
        clearTimeout(navClickLockTimer)
        navClickLockTimer = null
      }
      if (navClickScrollEndHandler) {
        window.removeEventListener('scrollend', navClickScrollEndHandler)
        navClickScrollEndHandler = null
      }
    }

    function activationLinePx() {
      var maxSm = NaN
      for (var t = 0; t < targets.length; t++) {
        var sm = readScrollMarginTopPx(targets[t])
        if (!isNaN(sm)) maxSm = isNaN(maxSm) ? sm : Math.max(maxSm, sm)
      }
      if (!isNaN(maxSm) && maxSm > 0) return maxSm
      return anchorViewportInsetPx(section, isFixed) + ANCHOR_SCROLL_GAP_PX
    }

    function updateScrollSpy() {
      if (!targets.length) return
      if (navClickLockHref) return
      var line = activationLinePx()
      var slop = 8
      var activeEl = null
      for (var i = 0; i < targets.length; i++) {
        if (targets[i].getBoundingClientRect().top <= line + slop) {
          activeEl = targets[i]
        }
      }
      if (!activeEl) activeEl = targets[0]
      var href = '#' + activeEl.id
      if (linkMap[href]) setActiveLink(links, linkMap[href])
    }

    function applyFixed() {
      isFixed = true
      section.classList.add(IS_FIXED_CLASS)
      syncAnchorScrollMargin(section, true)
      if (spacer) spacer.style.height = section.offsetHeight + 'px'
    }

    function removeFixed() {
      isFixed = false
      section.classList.remove(IS_FIXED_CLASS)
      section.classList.remove(IS_HIDDEN_CLASS)
      syncAnchorScrollMargin(section, false)
      if (spacer) spacer.style.height = '0px'
    }

    function checkScroll() {
      if (!isSticky) return

      if (!isFixed) {
        if (section.getBoundingClientRect().top <= 0) {
          applyFixed()
        }
      } else {
        var ref = spacer ? spacer : section
        if (ref.getBoundingClientRect().top >= 0) {
          removeFixed()
        }
      }

      if (isFixed && lastTarget) {
        var lastRect = lastTarget.getBoundingClientRect()
        var hideLine =
          Math.max(getStackedHeaderHeightPx(), section.offsetHeight) + ANCHOR_SCROLL_GAP_PX
        if (lastRect.bottom < hideLine) {
          section.classList.add(IS_HIDDEN_CLASS)
        } else {
          section.classList.remove(IS_HIDDEN_CLASS)
        }
      }

      updateScrollSpy()
    }

    function handleResize() {
      if (isFixed) {
        if (spacer) spacer.style.height = section.offsetHeight + 'px'
        syncAnchorScrollMargin(section, true)
      } else {
        syncAnchorScrollMargin(section, false)
      }
    }

    syncAnchorScrollMargin(section, false)
    window.addEventListener('scroll', checkScroll, { passive: true })
    window.addEventListener('resize', handleResize)
    checkScroll()

    function handleClick(e) {
      var link = e.target.closest('.oddit-section-anchor-links__link')
      if (!link) return
      e.preventDefault()

      setActiveLink(links, link)

      var targetId = (link.getAttribute('href') || '').trim()
      if (targetId && targetId !== '#') {
        var target = document.querySelector(targetId)
        if (target) {
          clearNavClickLock()
          navClickLockHref = targetId

          function finishClickScroll() {
            var href = navClickLockHref
            clearNavClickLock()
            if (href && linkMap[href]) {
              setActiveLink(links, linkMap[href])
            }
          }

          navClickScrollEndHandler = function () {
            finishClickScroll()
          }
          window.addEventListener('scrollend', navClickScrollEndHandler, { passive: true })

          navClickLockTimer = setTimeout(finishClickScroll, 900)

          syncAnchorScrollMargin(section, isFixed)
          target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }
    }

    nav.addEventListener('click', handleClick)

    section._odditAnchorCleanup = function () {
      clearNavClickLock()
      nav.removeEventListener('click', handleClick)
      window.removeEventListener('scroll', checkScroll)
      window.removeEventListener('resize', handleResize)
      removeFixed()
      document.documentElement.style.removeProperty('--oddit-anchor-scroll-margin')
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init()
    })
  } else {
    init()
  }

  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('.oddit-section-anchor-links')
    if (el) init(el)
  })

  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('.oddit-section-anchor-links')
    if (el && typeof el._odditAnchorCleanup === 'function') el._odditAnchorCleanup()
  })
})()
